import { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Card,
    Spin,
    Button,
    Input,
    Select,
    Row,
    Col,
    DatePicker,
    message,
    Avatar,
    Tooltip
} from "antd";
import { AuthContext } from "../../context/AuthContext";
import ViewEmpSalary from "../../components/ViewEmpSalary";
import { useNavigate } from "react-router-dom";
import EditOneEmployee from "../../components/EditOneEmployee";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UserOutlined, FilePdfOutlined, FileExcelOutlined, SearchOutlined, ClearOutlined } from "@ant-design/icons";
import { 
    Briefcase, 
    Building2, 
    Calendar, 
    Mail, 
    Phone, 
    User, 
    ChevronRight, 
    Layers, 
    Users, 
    Grid, 
    LayoutList,
    MapPin
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import ViewOneEmployeeRecord from "../../components/hr/ViewOneEmployeeRecord";
import EmployeeProfile from "../EmployeeProfile";
import "./TeamMembers.css";


const { Option } = Select;

// Modern Info Row using Lucide icons
const ModernInfoRow = ({ icon: Icon, label }) => (
    <div className="info-item">
        <Icon size={14} className="info-icon" />
        <span className="info-text">{label}</span>
    </div>
);

// Helper to get initials
const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
};

// Premium mesh-style gradients for placeholders
const placeholderGradients = [
    'radial-gradient(at 0% 0%, #0e497b 0, transparent 50%), radial-gradient(at 50% 0%, #1677ff 0, transparent 50%), radial-gradient(at 100% 0%, #002c5c 0, transparent 50%)',
    'radial-gradient(at 0% 0%, #1e3a8a 0, transparent 50%), radial-gradient(at 50% 0%, #3b82f6 0, transparent 50%), radial-gradient(at 100% 0%, #172554 0, transparent 50%)',
    'radial-gradient(at 0% 0%, #1e1b4b 0, transparent 50%), radial-gradient(at 50% 0%, #4338ca 0, transparent 50%), radial-gradient(at 100% 0%, #312e81 0, transparent 50%)',
    'radial-gradient(at 0% 0%, #0f172a 0, transparent 50%), radial-gradient(at 50% 0%, #334155 0, transparent 50%), radial-gradient(at 100% 0%, #020617 0, transparent 50%)',
];

const getGradient = (name = "") => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return placeholderGradients[Math.abs(hash) % placeholderGradients.length];
};

// Modern Employee Card Component (Matrimony Style)
const ModernEmployeeCard = ({ emp, onClick, isTL }) => {
    const fullName = [emp.first_name, emp.last_name].filter(Boolean).join(" ") || "—";
    
    // Normalize status for color mapping
    const rawStatus = (emp.status || "Active").toLowerCase();
    const statusKey = rawStatus === "confirmed" ? "confirmed" : 
                     rawStatus === "probation" ? "probation" : 
                     rawStatus === "resigned" ? "resigned" : "default";

    const statusColors = {
        confirmed: { bg: "rgba(82, 196, 26, 0.9)", text: "#ffffff" },
        probation: { bg: "rgba(250, 173, 20, 0.9)", text: "#ffffff" },
        resigned: { bg: "rgba(255, 77, 79, 0.9)", text: "#ffffff" },
        default: { bg: "rgba(22, 119, 255, 0.9)", text: "#ffffff" }
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.4 }}
            className={`glass-card ${isTL ? 'tl-card' : ''}`}
            onClick={onClick}
        >
            <div className="photo-container">
                {emp.photo ? (
                    <img src={emp.photo} alt={fullName} className="profile-photo" />
                ) : (
                    <div className="profile-photo" style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        background: getGradient(fullName),
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Subtle pattern overlay */}
                        <div style={{ 
                            position: 'absolute', 
                            top: 0, left: 0, right: 0, bottom: 0, 
                            opacity: 0.05,
                            backgroundImage: 'linear-gradient(45deg, #ffffff 25%, transparent 25%, transparent 75%, #ffffff 75%, #ffffff), linear-gradient(45deg, #ffffff 25%, transparent 25%, transparent 75%, #ffffff 75%, #ffffff)',
                            backgroundSize: '40px 40px',
                            backgroundPosition: '0 0, 20px 20px'
                        }} />
                        
                        <div style={{ 
                            fontSize: '56px', 
                            fontWeight: 900, 
                            letterSpacing: '4px',
                            textShadow: '0 8px 16px rgba(0,0,0,0.3)',
                            zIndex: 1,
                            fontFamily: 'Inter, sans-serif'
                        }}>
                            {getInitials(emp.first_name, emp.last_name)}
                        </div>
                    </div>
                )}
                
                <div className="status-badge-floating" style={{ 
                    background: statusColors[statusKey].bg, 
                    color: statusColors[statusKey].text,
                    border: 'none',
                    fontWeight: 900,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}>
                    {emp.status || "Active"}
                </div>

                <div className="photo-overlay">
                    <div className="overlay-name">
                        <span style={{ display: 'block', fontSize: '13px', fontWeight: 500, opacity: 0.9, marginBottom: -2 }}>
                            {emp.salutation || ''}
                        </span>
                        {fullName}
                    </div>
                </div>
            </div>

            <div className="card-info-section">
                <div className="designation-text" style={{ fontSize: '15px' }}>{emp.designation_name || "—"}</div>
                
                <div className="dept-text">
                    <Building2 size={14} strokeWidth={2} />
                    <span style={{ fontWeight: 500 }}>{emp.department_name || "—"}</span>
                </div>

                {emp.location && (
                    <div className="dept-text" style={{ marginTop: 2 }}>
                        <MapPin size={14} style={{ color: '#94a3b8' }} />
                        <span style={{ color: '#64748b' }}>{emp.location}</span>
                    </div>
                )}

                <div className="dept-text" style={{ marginTop: 4 }}>
                    <Layers size={14} />
                    <span style={{ color: '#94a3b8' }}>{emp.emp_code || emp.user?.username || "—"}</span>
                </div>
                
                <div style={{ marginTop: 'auto', paddingTop: 12 }}>
                    <div className="dept-text" style={{ fontSize: 11, opacity: 0.8 }}>
                        <Calendar size={12} />
                        <span>Member since {emp.date_of_joining ? dayjs(emp.date_of_joining).format("MMMM YYYY") : "—"}</span>
                    </div>
                </div>
            </div>

            <div className="card-action-bar">
                <button className="view-profile-btn">
                    View Full Profile
                    <ChevronRight size={14} />
                </button>
            </div>
        </motion.div>
    );
};

export default function TeamMembers({ isResigned }) {
    const { authData, backUrl } = useContext(AuthContext);
    const navigate = useNavigate();

    const [selectedEmployee, setSelectedEmployee] = useState(null)

    const [showSalary, setShowSalary] = useState(false);
    const [showEmp, setShowEmp] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);

    const [excelLoading, setExcelLoading] = useState(false);

    const [selectedStatus, setSelectedStatus] = useState(
        isResigned ? "Resigned" : "All"
    );

    const [pageSize, setPageSize] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [organizations, setOrganizations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [placesOfWork, setPlacesOfWork] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);
    const [selectedPlaceOfWork, setSelectedPlaceOfWork] = useState(null);
    const [selectedWorkLocation, setSelectedWorkLocation] = useState(null);

    const [searchCode, setSearchCode] = useState("");
    const [searchName, setSearchName] = useState("");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const [viewMode, setViewMode] = useState("hierarchy"); // "hierarchy" or "grid"

    const [showCalculator, setShowCalculator] = useState(false);
    const [particular, setParticular] = useState(null);

    // Helper function to parse JSON strings
    const parseJsonField = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        if (typeof field === 'string') {
            try {
                return JSON.parse(field);
            } catch (e) {
                return [];
            }
        }
        return [];
    };

    // Build query parameters for API calls
    const buildQueryParams = (includePagination = true) => {
        const params = new URLSearchParams();

        // Pagination parameters (only if requested)
        if (includePagination) {
            params.append("page", currentPage);
            params.append("page_size", pageSize);
        }

        // Status filter - only add if it's not "All"
        if (selectedStatus && selectedStatus !== "All") {
            params.append("status", selectedStatus);
        }

        // Search filters - trim whitespace
        if (searchCode && searchCode.trim()) {
            params.append("emp_code", searchCode.trim());
        }
        if (searchName && searchName.trim()) {
            params.append("employee_name", searchName.trim());
        }

        // Dropdown filters
        if (selectedOrg) {
            params.append("organisation", selectedOrg);
        }
        if (selectedDept) {
            params.append("department", selectedDept);
        }
        if (selectedDesig) {
            params.append("designation", selectedDesig);
        }
        if (selectedPlaceOfWork) {
            params.append("place_of_work", selectedPlaceOfWork);
        }
        if (selectedWorkLocation) {
            params.append("location", selectedWorkLocation);
        }

        // Date range filters
        if (fromDate) {
            params.append("from_date", dayjs(fromDate).format("YYYY-MM-DD"));
        }
        if (toDate) {
            params.append("to_date", dayjs(toDate).format("YYYY-MM-DD"));
        }

        return params.toString();
    };

    // Fetch employees with pagination
    const fetchEmployees = async () => {
        try {
            setLoading(true);
            const queryString = buildQueryParams(true);
            const url = `${backUrl}/api/emp/employees/hierarchy-list/?reporting_to=${authData?.employee?.id}${queryString ? `&${queryString}` : ''}`;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            setEmployees(res.data.results || res.data);
            setTotalCount(res.data.count || res.data.length || 0);
        } catch (error) {
            console.error("Error fetching employees:", error);
            message.error("Failed to fetch employees");
        } finally {
            setLoading(false);
        }
    };

    // Fetch organizations
    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganizations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching organizations:", error);
        }
    };

    // Fetch departments based on organization
    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`);
            setDepartments(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
        }
    };

    // Fetch designations based on department
    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`);
            setDesignations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching designations:", error);
            setDesignations([]);
        }
    };

    // Fetch places of work
    const fetchPlacesOfWork = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/worklocation/`);
            setPlacesOfWork(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching places of work:", error);
        }
    };

    // Initial load
    useEffect(() => {
        fetchOrganizations();
        fetchPlacesOfWork();
        fetchEmployees();
    }, []);

    // Fetch when page or page size changes
    useEffect(() => {
        if (currentPage > 1 || pageSize !== 50) {
            fetchEmployees();
        }
    }, [currentPage, pageSize]);

    // Helper to flatten array for Excel export
    const flattenArray = (arr, fields = []) => {
        if (!arr || !arr.length) return "-";
        return arr
            .map(item =>
                fields.map(f => `${f.label}: ${item[f.key] || "N/A"}`).join(", ")
            )
            .join(" | ");
    };

    // Helper to render values
    const renderValue = (value) => (value === null || value === undefined || value === "" ? "-" : value);

    // Handle Full & Final settlement
    const handleFullAndFinal = async (employeeId) => {
        try {
            setLoading(true);
            await axios.post(
                `${backUrl}/api/payroll/settlements/create/`,
                { employee_id: employeeId },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success("Full & Final settlement process triggered successfully.");
        } catch (error) {
            console.error("Error triggering settlement:", error);
            if (error.response?.data?.error) toast.error(` ${error.response.data.error}`);
            else toast.error("Failed to trigger Full & Final settlement. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Format row for export
    const formatRow = (row) => ({
        "Employee Code": row.user?.username || "-",
        "Salutation": row.salutation || "-",
        "Initial": row.initial || "-",
        "First Name": row.first_name || "-",
        "Surname": row.surname || "-",
        "Last Name": row.last_name || "-",
        "Organisation": row.organisation_name || "-",
        "Department": row.department_name || "-",
        "Designation": row.designation_name || "-",
        "Location": row.location || "-",
        "Place of Work": Array.isArray(row.place_of_work_names)
            ? row.place_of_work_names.join(", ")
            : row.place_of_work_names || "-",
        "Date of Joining": row.date_of_joining || "-",
        "Is HOD": row.is_hod ? "Yes" : "No",
        "Reporting To": row.reporting_to_name || "-",
        "Is HR": row.is_hr ? "Yes" : "No",
        "Is Superuser": row.is_superuser ? "Yes" : "No",
        "Role ID": row.role || "-",
        "Status": row.status || "-",
        "Probation End Date": row.probation_end_date || "-",
        "Is Active": row.is_active ? "Yes" : "No",
        "Is OT": row.is_ot ? "Yes" : "No",
        "Mobile Number": row.mobile_number || "-",
        "Email": row.user?.email || "-",
        "Date of Birth": row.date_of_birth || "-",
        "Place of Birth": row.place_of_birth || "-",
        "Mother Tongue": row.mother_tongue || "-",
        "Caste": row.caste || "-",
        "Gender": row.gender || "-",
        "Religion": row.religion || "-",
        "Blood Group": row.blood_group || "-",
        "Height": row.height || "-",
        "Weight": row.weight || "-",
        "Marital Status": row.marital_status || "-",
        "Wedding Date": row.wedding_date || "-",
        "Father Name": row.father_name || "-",
        "Father Profession": row.father_profession || "-",
        "Mother Name": row.mother_name || "-",
        "Mother Profession": row.mother_profession || "-",
        "Spouse Name": row.spouse_name || "-",
        "Spouse DOB": row.spouse_dob || "-",
        "Child Details": flattenArray(parseJsonField(row.child_details), [
            { key: "child_name", label: "Name" },
            { key: "child_dob", label: "DOB" },
            { key: "child_gender", label: "Gender" },
        ]),
        "Siblings Details": flattenArray(parseJsonField(row.sibling_details), [
            { key: "sibling_name", label: "Name" },
            { key: "sibling_dob", label: "DOB" },
            { key: "sibling_gender", label: "Gender" },
        ]),
        "Emergency Contact Person": row.emergency_contact_person || "-",
        "Emergency Contact Number": row.emergency_contact_number || "-",
        "Aadhaar Card Number": row.aadhaar_card_number || "-",
        "PAN Card Number": row.pan_card_number || "-",
        "Bank Name": row.bank_name || "-",
        "Bank Account Number": row.bank_account_number || "-",
        "Branch Name": row.branch_name || "-",
        "IFSC Code": row.ifsc_code || "-",
        "CUB Account Number": row.cub_account_number || "-",
        "UAN Number": row.uan_number || "-",
        "ESIC IP Number": row.esic_ip_number || "-",
        "Photo": row.photo || "-",
        "Address for Communication": row.address_for_communication || "-",
        "Area": row.area || "-",
        "City": row.city || "-",
        "State": row.state || "-",
        "Country": row.country || "-",
        "Pincode": row.pincode || "-",
        "Present Address": row.present_address ? "Yes" : "No",
        "Permanent Address": row.permanent_address_for_communication || "-",
        "Permanent City": row.permanent_city || "-",
        "Permanent State": row.permanent_state || "-",
        "Permanent Country": row.permanent_country || "-",
        "Permanent Pincode": row.permanent_pincode || "-",
        "Career History": Array.isArray(row.career_history) ? flattenArray(row.career_history, [
            { key: "organisation", label: "Organisation" },
            { key: "designation", label: "Designation" },
            { key: "work_duration_from", label: "From" },
            { key: "work_duration_to", label: "To" },
        ]) : (row.career_history || "-"),
        "Reference History": Array.isArray(row.reference_history) ? flattenArray(row.reference_history, [
            { key: "referal_name", label: "Name" },
            { key: "referal_desgination", label: "Designation" },
            { key: "referal_email_id", label: "Email" },
            { key: "referal_mobile_number", label: "Mobile" },
        ]) : (row.reference_history || "-"),
        "Education History": Array.isArray(row.education_history) ? flattenArray(row.education_history, [
            { key: "educational_qualification", label: "Qualification" },
            { key: "educational_institution_name", label: "Institution" },
            { key: "course_duration_from", label: "From" },
            { key: "course_duration_to", label: "To" },
        ]) : (row.education_history || "-"),
        "Date of Leaving": row.date_of_leaving || "-",
        "Last Working Day": row.last_working_day || "-",
        "Aadhaar Card Copy": row.aadhar_card_copy || "-",
        "Appointment Letter": row.appointment_letter || "-",
        "Bank Account Details": row.bank_account_details || "-",
        "COVID Certificate": row.covid_certificate || "-",
        "Degree Certificate": row.degree_certificate || "-",
        "Last 3 Month Payslip": row.last_three_month_payslip || "-",
        "Last 3 Months Account Statement": row.last_three_months_account_statement || "-",
        "Offer Letter": row.offer_letter || "-",
        "PAN Card Copy": row.pan_card_copy || "-",
        "Previous Experience Letter": row.previous_experience_letter || "-",
        "Relieving Letter": row.releiving_letter || "-",
        "Others 1": row.others_1 || "-",
        "Others 2": row.others2 || "-",
        "Landline Extension": row.landline_extension || "-",
        "Official CUG Number": row.official_cug_number || "-",
        "Official Email ID": row.official_email_id || "-",
        "Is Management": row.is_management || "-",
        "Is Special": row.is_special || "-",
        "Is Shift": row.is_shift || "-",
        "Is Active": row.is_active || "-",
    });

    // Export to Excel - fetches all data without pagination
    const handleExportExcel = async () => {
        try {
            setExcelLoading(true);
            message.loading({ content: 'Preparing Excel export...', key: 'excel' });

            // Build query without pagination
            const queryString = buildQueryParams(false);
            const url = `${backUrl}/api/emp/employees/${queryString ? `?${queryString}` : ''}`;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            const allEmployees = res.data.results || res.data;

            if (!allEmployees || allEmployees.length === 0) {
                message.warning({ content: 'No employees to export', key: 'excel' });
                return;
            }

            const XLSX = require("xlsx");
            const formatted = allEmployees.map(formatRow);
            const ws = XLSX.utils.json_to_sheet(formatted);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Employees");
            XLSX.writeFile(wb, `Employees_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`);

            message.success({ content: `Exported ${allEmployees.length} employees successfully!`, key: 'excel' });
        } catch (error) {
            console.error("Error exporting to Excel:", error);
            message.error({ content: 'Failed to export Excel file', key: 'excel' });
        } finally {
            setExcelLoading(false);
        }
    };

    // Export to PDF
    const handleExportPDF = () => {
        if (!employees.length) {
            toast.warning("No employees to export");
            return;
        }

        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
        const formatted = employees.map(formatRow);
        const columns = Object.keys(formatRow(employees[0] || {}));

        const columnStyles = {};
        columns.forEach((_, index) => (columnStyles[index] = { cellWidth: 80 }));

        autoTable(doc, {
            head: [columns],
            body: formatted.map((row) => Object.values(row)),
            styles: {
                fontSize: 8,
                cellPadding: 6,
                overflow: "linebreak",
                valign: "middle"
            },
            headStyles: {
                fillColor: [14, 73, 123],
                textColor: "#FFFFFF",
                halign: "center",
                fontSize: 9
            },
            theme: "grid",
            startY: 30,
            columnStyles: columnStyles,
            pageBreak: "auto",
            horizontalPageBreak: true,
            horizontalPageBreakRepeat: true,
        });

        doc.save(`Employees_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`);
    };

    // Table columns definition
    const columns = [
        {
            title: "Employee Code",
            dataIndex: ["user", "username"],
            key: "emp_code",
            fixed: 'left',
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                    <Avatar
                        size={50}
                        src={record.photo}
                        icon={!record.photo && <UserOutlined />}
                        style={{
                            backgroundColor: !record.photo ? '#1890ff' : undefined,
                            flexShrink: 0
                        }}
                    />
                    <a
                        onClick={() => {
                            setSelectedId(record.id);
                            setShowEmp(true);
                        }}
                        style={{ cursor: "pointer", color: '#1890ff' }}
                    >
                        {text}
                    </a>
                </div>
            ),
            height: 30,
            width: 180,
        },
        { title: "Initial", dataIndex: "initial", key: "initial", render: renderValue, width: 100 },
        { title: "First Name", dataIndex: "first_name", key: "first_name", render: renderValue },
        { title: "Surname", dataIndex: "surname", key: "surname", render: renderValue },
        { title: "Last Name", dataIndex: "last_name", key: "last_name", render: renderValue },
        
        { title: "Date of Joining", dataIndex: "date_of_joining", key: "date_of_joining", render: renderValue },
        { title: "Organisation", dataIndex: "organisation_name", key: "organisation_name", render: renderValue },
        { title: "Department", dataIndex: "department_name", key: "department_name", render: renderValue },
        { title: "Designation", dataIndex: "designation_name", key: "designation_name", render: renderValue },

    ];

    const onBack = () => {
        setShowEmp(false);
    };

    // Clear all filters and reset to initial state
    const handleClearFilters = () => {
        setSearchCode("");
        setSearchName("");
        setSelectedOrg(null);
        setSelectedDept(null);
        setSelectedDesig(null);
        setSelectedPlaceOfWork(null);
        setSelectedWorkLocation(null);
        setFromDate(null);
        setToDate(null);
        setSelectedStatus(isResigned ? "Resigned" : "All");
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        setPageSize(50);

        // Fetch employees with reset filters
        setTimeout(() => {
            fetchEmployees();
        }, 100);
    };

    // Apply filters
    const handleFilter = () => {
        setCurrentPage(1); // Reset to first page when filtering
        fetchEmployees();
    };

    return (
        <>
            <center><h4>Team Members</h4></center>
            <ToastContainer position="top-right" autoClose={3000} />
            {showEmp ? (
                selectedId !== 0 && <EmployeeProfile empId={selectedId} onBack={onBack} DisableEditOption={true} />
            ) : showCalculator ? (
                <>
                    <Button onClick={() => { setShowCalculator(false); setParticular(null); }} style={{ marginBottom: "10px" }}>
                        ← Back
                    </Button>
                    <ViewEmpSalary amount={particular.offered_salary} cid={particular.id} />
                </>
            ) : (
                <>
                    <Card bordered style={{ marginBottom: 20, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                        <Row style={{ marginBottom: 10 }}>
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<FileExcelOutlined />}
                                    onClick={handleExportExcel}
                                    loading={excelLoading}
                                    style={{ marginRight: 10, background: 'green', borderColor: 'green' }}
                                >

                                </Button>
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={handleFilter}
                                    style={{ marginRight: 10, backgroundColor: '#0e497b', borderColor: '#0e497b' }}
                                >
                                    Filter
                                </Button>
                                <Button
                                    onClick={handleClearFilters}
                                    style={{ backgroundColor: '#f5f5f5', color: '#000' }}
                                >
                                    Clear Filters
                                </Button>
                            </Col>
                        </Row>

                        <Row gutter={16} style={{ marginBottom: 10 }}>
                            <Col span={6}>
                                <Input
                                    allowClear
                                    placeholder="Search Employee Code"
                                    value={searchCode}
                                    onChange={(e) => setSearchCode(e.target.value)}
                                    onPressEnter={handleFilter}
                                />
                            </Col>
                            <Col span={6}>
                                <Input
                                    allowClear
                                    placeholder="Search Employee Name"
                                    value={searchName}
                                    onChange={(e) => setSearchName(e.target.value)}
                                    onPressEnter={handleFilter}
                                />
                            </Col>
                            <Col span={6}>
                                <Select
                                    allowClear
                                    showSearch
                                    placeholder="Select Organization"
                                    style={{ width: "100%" }}
                                    value={selectedOrg}
                                    onChange={(val) => {
                                        setSelectedOrg(val);
                                        setSelectedDept(null);
                                        setSelectedDesig(null);
                                        setDepartments([]);
                                        setDesignations([]);
                                        if (val) fetchDepartments(val);
                                    }}
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {organizations.map((o) => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                                </Select>
                            </Col>
                            <Col span={6}>
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select Department"
                                    style={{ width: "100%" }}
                                    value={selectedDept}
                                    onChange={(val) => {
                                        setSelectedDept(val);
                                        setSelectedDesig(null);
                                        setDesignations([]);
                                        if (val) fetchDesignations(val);
                                    }}
                                    disabled={!selectedOrg}
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {departments.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                                </Select>
                            </Col>
                        </Row>

                        <Row gutter={16} style={{ marginBottom: 10 }}>
                            <Col span={6}>
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select Designation"
                                    style={{ width: "100%" }}
                                    value={selectedDesig}
                                    onChange={(val) => setSelectedDesig(val)}
                                    disabled={!selectedDept}
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {designations.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                                </Select>
                            </Col>
                            <Col span={6}>
                                <Select
                                    allowClear
                                    placeholder="Select Work Location"
                                    style={{ width: "100%" }}
                                    value={selectedWorkLocation}
                                    onChange={(val) => setSelectedWorkLocation(val)}
                                >
                                    <Option value="Head Office">Head Office</Option>
                                    <Option value="Site Office">Site Office</Option>
                                </Select>
                            </Col>
                            <Col span={6}>
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select Place of Work"
                                    style={{ width: "100%" }}
                                    value={selectedPlaceOfWork}
                                    onChange={(val) => setSelectedPlaceOfWork(val)}
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {placesOfWork.map((p) => <Option key={p.id} value={p.id}>{p.name}</Option>)}
                                </Select>
                            </Col>
                            <Col span={6}>
                                <Select
                                    allowClear
                                    placeholder="Select Employment Status"
                                    style={{ width: "100%" }}
                                    value={selectedStatus}
                                    onChange={setSelectedStatus}
                                >
                                    <Option value="All">All</Option>
                                    <Option value="Confirmed">Confirmed</Option>
                                    <Option value="Probation">Probation</Option>
                                    <Option value="Resigned">Resigned</Option>
                                </Select>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={6}>
                                <DatePicker
                                    style={{ width: "100%" }}
                                    placeholder="From Date (Joining)"
                                    value={fromDate}
                                    onChange={(date) => setFromDate(date)}
                                    allowClear
                                    format="YYYY-MM-DD"
                                />
                            </Col>
                            <Col span={6}>
                                <DatePicker
                                    style={{ width: "100%" }}
                                    placeholder="To Date (Joining)"
                                    value={toDate}
                                    onChange={(date) => setToDate(date)}
                                    allowClear
                                    format="YYYY-MM-DD"
                                />
                            </Col>
                        </Row>
                    </Card>

                    {loading ? (
                        <div style={{ textAlign: "center", padding: "100px" }}>
                            <Spin size="large" tip="Loading Team..." />
                        </div>
                    ) : employees.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "100px", background: "#f8fafc", borderRadius: 16 }}>
                            <Users size={64} style={{ color: "#cbd5e1", marginBottom: 16 }} />
                            <p style={{ fontSize: 18, color: "#64748b", fontWeight: 500 }}>No team members found</p>
                        </div>
                    ) : (
                        <div className="team-container">
                            <div style={{ 
                                marginBottom: 24, 
                                display: "flex", 
                                alignItems: "center", 
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 16
                            }}>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: 20, color: "#1e293b", fontWeight: 700 }}>
                                        Team Members
                                    </h2>
                                    <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>
                                        Showing {employees.length} of {totalCount} members
                                    </p>
                                </div>

                                <div className="view-switcher">
                                    <div 
                                        className={`switch-btn ${viewMode === "hierarchy" ? "active" : ""}`}
                                        onClick={() => setViewMode("hierarchy")}
                                    >
                                        <Layers size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
                                        Hierarchy
                                    </div>
                                    <div 
                                        className={`switch-btn ${viewMode === "grid" ? "active" : ""}`}
                                        onClick={() => setViewMode("grid")}
                                    >
                                        <Grid size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
                                        Grid View
                                    </div>
                                </div>
                            </div>

                            <AnimatePresence mode="wait">
                                {viewMode === "hierarchy" ? (
                                    <motion.div
                                        key="hierarchy"
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {/* Grouping logic for hierarchy */}
                                        {(() => {
                                            const hodId = authData?.employee?.id;
                                            const directReports = employees.filter(e => e.reporting_to === hodId);
                                            const indirectReports = employees.filter(e => e.reporting_to !== hodId);

                                            // People who are TLs (have people reporting to them)
                                            const tls = directReports.filter(tl => 
                                                indirectReports.some(ir => ir.reporting_to === tl.id)
                                            );

                                            // People who report to HOD but have no subordinates
                                            const directTeam = directReports.filter(dr => 
                                                !tls.some(tl => tl.id === dr.id)
                                            );

                                            return (
                                                <>
                                                    {/* TL Teams */}
                                                    {tls.map(tl => (
                                                        <div key={tl.id} style={{ marginBottom: 40 }}>
                                                            <div className="team-group-header">
                                                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                                    <Avatar src={tl.photo} icon={<UserOutlined />} />
                                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <span style={{ fontWeight: 700, color: "#0e497b", fontSize: 16 }}>
                                                                            {tl.first_name} {tl.last_name}
                                                                        </span>
                                                                        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>
                                                                            {tl.department_name || 'Department'}
                                                                        </span>
                                                                    </div>
                                                                    <span className="tl-badge">TEAM LEAD</span>
                                                                </div>
                                                                <div style={{ fontSize: 12, color: "#64748b" }}>
                                                                    {indirectReports.filter(ir => ir.reporting_to === tl.id).length} Members
                                                                </div>
                                                            </div>
                                                            
                                                            <Row gutter={[20, 20]}>
                                                                {/* Show the TL card first */}
                                                                <Col xs={24} sm={12} md={8} lg={6}>
                                                                    <ModernEmployeeCard 
                                                                        emp={tl} 
                                                                        isTL={true}
                                                                        onClick={() => { setSelectedId(tl.id); setShowEmp(true); }} 
                                                                    />
                                                                </Col>
                                                                {/* Show their team members */}
                                                                {indirectReports
                                                                    .filter(ir => ir.reporting_to === tl.id)
                                                                    .map(member => (
                                                                        <Col key={member.id} xs={24} sm={12} md={8} lg={6}>
                                                                            <ModernEmployeeCard 
                                                                                emp={member} 
                                                                                onClick={() => { setSelectedId(member.id); setShowEmp(true); }} 
                                                                            />
                                                                        </Col>
                                                                    ))
                                                                }
                                                            </Row>
                                                        </div>
                                                    ))}

                                                    {/* Direct Reports to HOD (who are not TLs) */}
                                                    {directTeam.length > 0 && (
                                                        <div style={{ marginBottom: 40 }}>
                                                            <div className="team-group-header">
                                                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                                    <Users size={20} color="#0e497b" />
                                                                    <span style={{ fontWeight: 700, color: "#0e497b" }}>Direct Reports</span>
                                                                </div>
                                                            </div>
                                                            <Row gutter={[20, 20]}>
                                                                {directTeam.map(dr => (
                                                                    <Col key={dr.id} xs={24} sm={12} md={8} lg={6}>
                                                                        <ModernEmployeeCard 
                                                                            emp={dr} 
                                                                            onClick={() => { setSelectedId(dr.id); setShowEmp(true); }} 
                                                                        />
                                                                    </Col>
                                                                ))}
                                                            </Row>
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="grid"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <Row gutter={[20, 20]}>
                                            {employees.map((emp) => (
                                                <Col key={emp.id} xs={24} sm={12} md={8} lg={6}>
                                                    <ModernEmployeeCard 
                                                        emp={emp} 
                                                        onClick={() => { setSelectedId(emp.id); setShowEmp(true); }} 
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Pagination */}
                            <div style={{ textAlign: "center", marginTop: 24 }}>
                                <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                                    <Button
                                        disabled={currentPage === 1}
                                        onClick={() => { setCurrentPage(p => p - 1); }}
                                        style={{ borderColor: "#0e497b", color: "#0e497b" }}
                                    >
                                        ← Prev
                                    </Button>
                                    <span style={{ padding: "0 12px", color: "#0e497b", fontWeight: 600 }}>
                                        Page {currentPage} · {totalCount} total
                                    </span>
                                    <Button
                                        disabled={employees.length < pageSize}
                                        onClick={() => { setCurrentPage(p => p + 1); }}
                                        style={{ borderColor: "#0e497b", color: "#0e497b" }}
                                    >
                                        Next →
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
}